/**
 * Shared funding-bucket row editor — one editable `funding_tranches` row
 * (amount / Pending-Funded / MOD / FWA / date / comments).
 *
 * ON-SCREEN the funded state says "Funded" (client 2026-08-02, one word per
 * concept: funded = money-in, approved = money-out). The stored field, the
 * API enum, and the s-approved chip class are all unchanged.
 *
 * Extracted from EditPcoModal so the create forms can share the same
 * validation and normalization (incl. `normalizeModNumber` zero-padding) —
 * those can never drift between "create" and "edit".
 *
 * FUNDED IS STORED, AND THE REFERENCES ARE INDEPENDENT OF IT (client ruling
 * 2026-08-06). The status toggle writes `funding_tranches.is_funded`; MOD /
 * FWA / funded date persist whatever the PM typed, in EITHER state. The GC
 * hands those numbers over before the funding executes, so "Pending, refs
 * known" is a real and common bucket — and a mis-click on Pending no longer
 * costs anyone their references. What used to live here: a null-the-refs
 * branch in `bodyForRow`, a filled amber panel announcing it, and a lock that
 * grayed the three fields out while Pending. All gone, stylesheet included.
 *
 * THREE exports serve two contexts (client mockup ruling 2026-08-05):
 *
 * - `BucketRowEditor` — the full editor, "Edit details" only. A bucket there
 *   carries live money, so every field is the point.
 * - `BucketCreateFields` — the CREATE forms' bucket body: Amount + Comments +
 *   the Status toggle, with the reference fields revealed once the bucket
 *   says Funded (or already holds reference text). Creation is deliberately
 *   minimal; the full editor is where a pending bucket gets its known refs.
 * - `BucketRemoveButton` — the remove affordance the create forms put in
 *   their section-card head.
 *
 * Status control (client verdict 2026-07-28): the chip is DISPLAY ONLY in
 * both states. Making the chip itself the toggle was tried and rejected —
 * "not intuitive at all to click PENDING to switch to APPROVED"; a chip reads
 * as a badge, not a switch. Changing the state is an explicit, labeled
 * [Pending][Funded] segmented control in the full editor's field grid.
 */
import { Trash2 } from 'lucide-react';
import { normalizeModNumber } from '@coffman-tm/shared';
import { formatMoneyCents } from '../../lib/format';
import { MoneyInput } from '../MoneyInput';
import { ExpandChevron, SegButton } from '../design';
import type { FundingTrancheWriteRequest } from '@coffman-tm/shared';

/** One editable funding bucket row (maps to a funding_tranches row). */
export interface BucketRow {
  /** Stable React key. */
  key: string;
  /** Existing tranche id, or null for a not-yet-created bucket. */
  trancheId: string | null;
  amountDollars: string;
  /** Segmented Pending(false) / Approved(true). */
  approved: boolean;
  mod: string;
  fwa: string;
  approvedDate: string;
  comments: string;
  /** UI-only: expanded to the full field grid vs. the one-line summary. */
  expanded: boolean;
}

export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function newKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `row-${Math.random().toString(36).slice(2)}`;
}

/** A fresh, empty bucket row — starts expanded so the fields are ready to fill. */
export function blankPendingRow(): BucketRow {
  return {
    key: newKey(),
    trancheId: null,
    amountDollars: '',
    approved: false,
    mod: '',
    fwa: '',
    approvedDate: '',
    comments: '',
    expanded: true,
  };
}

/** Parse a MoneyInput raw string to integer cents (blank -> 0). */
export function parseAmountCents(raw: string): number {
  const t = raw.trim();
  if (t === '') return 0;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

/**
 * The tranche write body a row saves as. The status writes `is_funded`; the
 * references write themselves, in either state — a Pending bucket keeps the
 * MOD / FWA / date the GC has already issued (client ruling 2026-08-06).
 */
export function bodyForRow(r: BucketRow): FundingTrancheWriteRequest {
  return {
    approved_amount_cents: parseAmountCents(r.amountDollars),
    mod_number: r.mod.trim() || null,
    hoffman_fwa: r.fwa.trim() || null,
    approved_date: r.approvedDate.trim() || null,
    is_funded: r.approved,
    comments: r.comments.trim() || null,
  };
}

/** A not-yet-created row with nothing meaningful in it — skip on save. */
export function isBlankNewRow(r: BucketRow): boolean {
  return (
    r.trancheId === null &&
    parseAmountCents(r.amountDollars) === 0 &&
    !r.mod.trim() &&
    !r.fwa.trim() &&
    !r.approvedDate.trim() &&
    !r.comments.trim()
  );
}

/**
 * The remove affordance for a create form's bucket, right-aligned in the
 * section-card head (`marginLeft: 'auto'`). It used to carry a status chip
 * and a live amount too; both were cut on sight (Brandon 2026-08-05) — the
 * amount is the field directly below, and the status is the toggle beside it.
 */
export function BucketRemoveButton({
  index,
  busy,
  onRemove,
}: {
  index: number;
  busy: boolean;
  onRemove: () => void;
}) {
  return (
    <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}>
      <button
        type="button"
        className="icon-btn danger"
        aria-label={`Remove bucket ${index + 1}`}
        title="Remove bucket"
        onClick={onRemove}
        disabled={busy}
      >
        <Trash2 size={14} />
      </button>
    </span>
  );
}

/**
 * The CREATE forms' bucket body: Amount paired with Comments, then the
 * [Pending][Funded] Status toggle. The reference fields (MOD / FWA, and the
 * funded date where the caller shows it) appear only once the bucket says
 * Funded — or while they still hold text, so a flip back to Pending never
 * hides values the PM typed. Since 2026-08-06 those values also SURVIVE that
 * flip; the reveal rule is unchanged, but it is now about keeping the create
 * form minimal rather than about protecting data from a clearing save.
 *
 * Brandon 2026-08-05, reversing the amount-only cut from earlier the same
 * day: "if it can only ever be Pending, why even have Pending listed" — the
 * toggle earns its place by letting an already-executed PCO be created
 * Funded, with its MOD / FWA, in one motion.
 */
export function BucketCreateFields({
  row,
  index = 0,
  busy,
  onChange,
  amountLabel = 'Amount',
  showFundedDate = false,
}: {
  row: BucketRow;
  index?: number;
  busy: boolean;
  onChange: (patch: Partial<BucketRow>) => void;
  amountLabel?: string;
  showFundedDate?: boolean;
}) {
  const refsDirty = !!(row.mod.trim() || row.fwa.trim() || row.approvedDate.trim());
  const refsOnScreen = row.approved || refsDirty;
  return (
    <>
      <div className="new-pco-row">
        <div className="new-pco-field">
          <label className="field-label">{amountLabel}</label>
          <MoneyInput
            className="field-input mono"
            value={row.amountDollars}
            onChange={(v) => onChange({ amountDollars: v })}
            disabled={busy}
            placeholder="0.00"
          />
        </div>
        <div className="new-pco-field">
          <label className="field-label">Comments</label>
          <input
            className="field-input"
            value={row.comments}
            onChange={(e) => onChange({ comments: e.target.value })}
            disabled={busy}
            placeholder="optional"
          />
        </div>
      </div>
      <div className="new-pco-row">
        <div className="new-pco-field">
          <label className="field-label">Status</label>
          <div
            role="group"
            aria-label={`Bucket ${index + 1} status`}
            style={{ display: 'inline-flex' }}
          >
            <SegButton
              active={!row.approved}
              disabled={busy}
              onClick={() => onChange({ approved: false })}
              position="left"
            >
              Pending
            </SegButton>
            <SegButton
              active={row.approved}
              disabled={busy}
              onClick={() => onChange({ approved: true })}
              position="right"
            >
              Funded
            </SegButton>
          </div>
        </div>
      </div>
      {refsOnScreen && (
        <div className="new-pco-row">
          <div className="new-pco-field">
            <label className="field-label">MOD #</label>
            <input
              className="field-input mono"
              value={row.mod}
              onChange={(e) => onChange({ mod: e.target.value })}
              onBlur={() => {
                // Reflect the API's storage normalization (zero-padded 3
                // digits) so the PM sees on-screen what will actually save.
                const normalized = normalizeModNumber(row.mod) ?? '';
                if (normalized !== row.mod) onChange({ mod: normalized });
              }}
              disabled={busy}
              placeholder="002"
            />
          </div>
          <div className="new-pco-field">
            <label className="field-label">FWA #</label>
            <input
              className="field-input mono"
              value={row.fwa}
              onChange={(e) => onChange({ fwa: e.target.value })}
              disabled={busy}
              placeholder="WA 123-003"
            />
          </div>
        </div>
      )}
      {refsOnScreen && showFundedDate && (
        <div className="new-pco-row">
          <div className="new-pco-field">
            <label className="field-label">Funded date</label>
            <input
              type="date"
              className="field-input mono"
              value={row.approvedDate}
              onChange={(e) => onChange({ approvedDate: e.target.value })}
              disabled={busy}
            />
          </div>
          <div className="new-pco-field" />
        </div>
      )}
    </>
  );
}

export function BucketRowEditor({
  row,
  index,
  busy,
  onChange,
  onRemove,
  amountLabel,
  showApprovedDate = true,
}: {
  row: BucketRow;
  index: number;
  busy: boolean;
  onChange: (patch: Partial<BucketRow>) => void;
  onRemove: () => void;
  /** Override the per-row amount field label (default "Bucket {n} amount"). */
  amountLabel?: string;
  /**
   * Show the Approved date field. Callers that don't manage execution dates
   * hide it; the column is untouched either way — a hidden field saves null.
   */
  showApprovedDate?: boolean;
}) {
  const amountCents = parseAmountCents(row.amountDollars);

  // Collapsed one-line reference summary. A PENDING bucket can carry these
  // too — the GC issues the refs before the money executes — so the summary
  // is keyed off what's filled in, never off the status.
  const summaryRefs: string[] = [];
  if (row.mod.trim()) summaryRefs.push(`MOD ${row.mod.trim()}`);
  if (row.fwa.trim()) summaryRefs.push(row.fwa.trim());
  if (row.approvedDate.trim()) summaryRefs.push(row.approvedDate.trim());

  const toggle = () => onChange({ expanded: !row.expanded });

  // Renders as its own .pco-card, nested inside EditPcoModal's "Funding
  // buckets" card by design — not a bug to flatten. See
  // docs/system/04-pcos-and-funding.md's 2026-08-06 closure note.
  return (
    <div className="pco-card">
      {/* Disclosure header — the whole row is the expand/collapse control
          (click or Enter/Space). The delete button stops propagation on both
          click and keydown so it never leaks into this toggle. */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={row.expanded}
        aria-label={`Bucket ${index + 1}, ${row.approved ? 'Funded' : 'Pending'}, ${formatMoneyCents(
          amountCents,
        )} — ${row.expanded ? 'collapse' : 'expand to edit'}`}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '9px 10px',
          cursor: 'pointer',
          background: 'var(--paper)',
        }}
        title={row.expanded ? 'Click to collapse' : 'Click to expand'}
      >
        <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}>
          <ExpandChevron expanded={row.expanded} size={13} />
        </span>
        {/* Display only, in BOTH states. The chip reports the bucket's status;
            it never changes it. The control that does is the labeled Status
            segmented toggle in the expanded body below. */}
        <span className={`status-chip ${row.approved ? 's-approved' : 's-pending'}`}>
          {row.approved ? 'Funded' : 'Pending'}
        </span>
        <span
          className="mono"
          style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}
        >
          {formatMoneyCents(amountCents)}
        </span>
        {!row.expanded && summaryRefs.length > 0 && (
          <span
            className="mono"
            style={{
              fontSize: 12,
              color: 'var(--ink-3)',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {summaryRefs.join('  ·  ')}
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {!row.expanded && (
            <span className="btn-link" aria-hidden style={{ fontSize: 12 }}>
              Edit
            </span>
          )}
          <button
            type="button"
            className="icon-btn danger"
            aria-label={`Remove bucket ${index + 1}`}
            title="Remove bucket"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            disabled={busy}
          >
            <Trash2 size={14} />
          </button>
        </span>
      </div>

      {row.expanded && (
        <div
          style={{
            padding: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            borderTop: '1px solid var(--rule)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            {/* new-pco-field is what scopes .field-label's eyebrow + .field-input's
                underline — without it the label renders inline and collides with
                the input, which also loses its editable look (found live 2026-08-06). */}
            <div className="new-pco-field" style={{ flex: '1 1 140px', minWidth: 120 }}>
              <label className="field-label">{amountLabel ?? `Bucket ${index + 1} amount`}</label>
              <MoneyInput
                className="field-input mono"
                value={row.amountDollars}
                onChange={(v) => onChange({ amountDollars: v })}
                disabled={busy}
                placeholder="0.00"
              />
            </div>
          </div>

          {/* The one control that sets Pending/Funded. Explicitly labeled and
              parked in the field grid above the references — a segmented pair
              reads as "pick one", which a single clickable chip did not. It
              gates nothing: the fields below stand on their own. */}
          <div className="new-pco-row">
            <div className="new-pco-field">
              <label className="field-label">Status</label>
              <div
                role="group"
                aria-label={`Bucket ${index + 1} status`}
                style={{ display: 'inline-flex' }}
              >
                <SegButton
                  active={!row.approved}
                  disabled={busy}
                  onClick={() => onChange({ approved: false })}
                  position="left"
                >
                  Pending
                </SegButton>
                <SegButton
                  active={row.approved}
                  disabled={busy}
                  onClick={() => onChange({ approved: true })}
                  position="right"
                >
                  Funded
                </SegButton>
              </div>
            </div>
          </div>

          {/* Always editable, in BOTH states, and they persist either way
              (client ruling 2026-08-06). The GC commonly issues an FWA — and
              often a MOD — before the funding executes, so a Pending bucket
              that already knows its references is an ordinary bucket, not a
              contradiction. These fields used to sit grayed and inert while
              Pending, and get nulled on save; both are gone. */}
          <div className="new-pco-row">
            <div className="new-pco-field">
              <label className="field-label">MOD #</label>
              <input
                className="field-input mono"
                value={row.mod}
                onChange={(e) => onChange({ mod: e.target.value })}
                onBlur={() => {
                  // Reflect the API's storage normalization (zero-padded 3
                  // digits) so the PM sees on-screen what will actually save.
                  const normalized = normalizeModNumber(row.mod) ?? '';
                  if (normalized !== row.mod) onChange({ mod: normalized });
                }}
                disabled={busy}
                placeholder="002"
              />
            </div>
            <div className="new-pco-field">
              <label className="field-label">FWA #</label>
              <input
                className="field-input mono"
                value={row.fwa}
                onChange={(e) => onChange({ fwa: e.target.value })}
                disabled={busy}
                placeholder="WA 123-003"
              />
            </div>
          </div>
          <div className="new-pco-row">
            {showApprovedDate && (
              <div className="new-pco-field">
                <label className="field-label">Funded date</label>
                <input
                  type="date"
                  className="field-input mono"
                  value={row.approvedDate}
                  onChange={(e) => onChange({ approvedDate: e.target.value })}
                  disabled={busy}
                />
              </div>
            )}
            <div className="new-pco-field">
              <label className="field-label">Comments</label>
              <input
                className="field-input"
                value={row.comments}
                onChange={(e) => onChange({ comments: e.target.value })}
                disabled={busy}
                placeholder="optional"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
